// There was an effort to break this file apart, but that was abandoned and we now actually want it globally available.
// Other context files for similar data:
// ./manage-subscription-drawer-context.js - Used in the Manage Subscription Drawer/Modal and it's associated components
// ./payment-and-subscription-page-context.js - Used in the Payment and Subscription Page and it's associated components

import React, { createContext, useMemo, useEffect, useCallback, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { NotificationService } from 'ui-components/services/notification-service';

import { emptyObject, emptyArray } from 'ui-components/default-props/empty';
import { ChildrenPropType } from '../../../../prop-types/children';
import { LogService } from '../../../../services/log-service';
import Global from '../../../../store/global-selectors';
import { getBillingInfo, cancelSubscription, remove3plBundle, manageSubscription } from '../../api/subscription';
import { CANCEL_ACCOUNT_PAGE, CANCEL_ACCOUNT_VIA_SUPPORT, DEFAULT_CANCEL_REASON_ID } from '../constants';
import { cancellationRequestErrorNotification } from '../constants/notifications';
import { useIs3PLPlanEnabledForSeller } from '../../../../hooks/use-is-3pl-plan-enabled-for-seller';
import {
  removeProviderBundleSuccessNotification,
  removeProviderBundleFailureNotification,
} from '../sections/3pl-provider-bundle/constants';
import { BillingPlanIds } from '../../../../enums/billing-plan-ids';
import {
  sellerSubscriptionPlanInfoUpdated,
  refreshSellerPlanAndSubscriptionInfo,
  updateSellerSubscriptionData,
} from '../../../../store/seller/seller.actions';
import { Segment } from '../../../../services/analytics-services/segment';
import { SUBSCRIPTION_CANCELLATION_COMPLETE } from '../../../../constants/analytics-events';
import { useIsLosingNonWalletAccess, useIsNonWalletAccessVisible } from '../../../../hooks/non-wallet-access-hooks';
import { getPaymentSummaryListApi } from '../../../../financial-services-ui/api';
import { setPaymentMethods } from '../../../../store/payment-methods/payment-methods.actions';
import { TransactionCategory } from '@shipstation/financial-services-ui-components';

export const PaymentAndSubscriptionMegaContext = createContext({});

/**
 * @typedef {import('../../../../financial-services-ui/types/fsui').PaymentMethodDetails} PaymentMethodDetails
 */

/**
 * @returns {{
 * paymentMethodDetails: PaymentMethodDetails[]
 * isPlanInfoLoading: boolean
 * isPaymentSummaryListLoading: boolean
 * reloadData: () => Promise<[any, void | PaymentMethodDetails[]]>
 * }}
 */
export const usePaymentAndSubscriptionMegaContext = () => useContext(PaymentAndSubscriptionMegaContext);

const PROGRAMMATIC_PENDING_CLIENT_CANCELLATION_FAILURE_ERROR_CODE = 'ThreePlPlanRemovalFailed';
const extractErrorDataFromRemovalFailureResponse = (error) => {
  const { Errors: arrayOfErrors = [] } = error.response.data ?? {};
  const failedToCancelPendingInvitesErrorObject = arrayOfErrors.find(
    ({ Code }) => Code === PROGRAMMATIC_PENDING_CLIENT_CANCELLATION_FAILURE_ERROR_CODE,
  );
  return failedToCancelPendingInvitesErrorObject;
};

export const PaymentAndSubscriptionMegaContextProvider = ({ children }) => {
  // state
  const dispatch = useDispatch();
  const [billingInfo, setBillingInfo] = useAsyncSafeState(undefined);
  const [isBillingInfoLoading, setIsBillingInfoLoading] = useAsyncSafeState(true);
  const [isPaymentSummaryListLoading, setIsPaymentSummaryListLoading] = useAsyncSafeState(false);
  const [isSubscriptionDataLoading, setIsSubscriptionDataLoading] = useAsyncSafeState(true);
  const [isCancellationModalOpen, setIsCancellationModalOpen] = useAsyncSafeState(false);
  const [desiredPlanCode, setDesiredPlanCode] = useAsyncSafeState('');
  const [desiredNumberOfAdditionalUsers, setDesiredNumberOfAdditionalUsers] = useAsyncSafeState(0);
  const [followUpCancelReasonId, setFollowUpCancelReasonId] = useAsyncSafeState(DEFAULT_CANCEL_REASON_ID);
  const [currentPage, setCurrentPage] = useAsyncSafeState(CANCEL_ACCOUNT_PAGE);
  const [followUpExplain, setFollowUpExplain] = useAsyncSafeState('');
  const [cancelReasonId, setCancelReasonId] = useAsyncSafeState(DEFAULT_CANCEL_REASON_ID);
  const [comments, setComments] = useAsyncSafeState('');
  const [cancellationInfo, setCancellationInfo] = useAsyncSafeState(emptyObject);
  const [sellerStampsProviders, setSellerStampsProviders] = useAsyncSafeState(emptyArray);
  const [isBillingEmailModalOpen, setIsBillingEmailModalOpen] = useAsyncSafeState(false);
  const [is3plPlanEnabled, setIs3plPlanEnabled] = useAsyncSafeState(useIs3PLPlanEnabledForSeller());
  const [errorDuring3plPlanRemoval, setErrorDuring3plPlanRemoval] = useAsyncSafeState();
  const [billingInfoErrorType, setBillingInfoErrorType] = useAsyncSafeState(null);
  const [isUserAddOnModalOpen, setIsUserAddOnModalOpen] = useAsyncSafeState(false);
  const [pendingDateTimeUTC, setPendingDateTimeUTC] = useAsyncSafeState(undefined); // only used to show most updated value for the Your Carriers Deactivated modal
  const shouldShowByoaToggle = useIsNonWalletAccessVisible();
  const isLosingByoaAccess = useIsLosingNonWalletAccess();
  const [byoaEnabled, setByoaEnabled] = useAsyncSafeState(shouldShowByoaToggle && !isLosingByoaAccess);
  const subscriptionData = useSelector(Global.selectSellerSubscriptionData);
  const { allPlans, currentBillingPlan, suggestedPlan, currentSubscription, planToNonWalletAccessPlanMap } = subscriptionData;
  const currentBillingPlanId = currentBillingPlan?.billingPlanId;
  const { pendingPlanCode } = currentSubscription ?? {};
  // Note: check if the pendingPlanCode is in the allPlans array
  const pendingBillingPlanId = (allPlans ?? emptyArray).find((plan) => plan.code === pendingPlanCode)?.billingPlanId;
  // Note: if pendingBillingPlanId is undefined, check if the pendingPlanCode is in the planToNonWalletAccessPlanMap
  const [convertedPendingPlanId] =
    Object.entries(planToNonWalletAccessPlanMap ?? {}).find(([, { code }]) => pendingPlanCode === code) ?? [];
  const pendingPlanIdInGrid = pendingBillingPlanId || convertedPendingPlanId;
  const suggestedPlanId = suggestedPlan?.planID;
  const defaultSuggestedPlanId =
    !!currentBillingPlanId && parseInt(currentBillingPlanId, 10) !== BillingPlanIds.Trial ? currentBillingPlanId : suggestedPlanId;
  const [selectedPlanId, setSelectedPlanId] = useAsyncSafeState(pendingPlanIdInGrid ?? defaultSuggestedPlanId);
  const isPlanInfoLoading = isSubscriptionDataLoading;
  /** @type {[PaymentMethodDetails[], (value: PaymentMethodDetails[]) => void]} */
  const [paymentMethodDetails, setPaymentMethodDetails] = useAsyncSafeState(emptyArray);

  /** @type {boolean} does Recurly have a credit card on file for the seller (is legacy billing approach) */
  const hasLegacyRecurlyBillingInfo = useMemo(() => {
    return Boolean(billingInfo && billingInfo.accountCode && !billingInfo.isTokenized);
  }, [billingInfo]);

  /** @type {boolean} does the seller lack a payment method (checks legacy approach and modern Auctane Pay approach) */
  const isMissingPaymentMethod = useMemo(() => {
    return !billingInfo?.creditCard && paymentMethodDetails.length === 0;
  }, [billingInfo, paymentMethodDetails]);

  const hasActiveFreightPaymentMethod = paymentMethodDetails.some((pm) => pm.assignedPreferences.includes(TransactionCategory.Freight));

  // callbacks
  const closeUserAddonModal = useCallback(() => {
    setIsUserAddOnModalOpen(false);
  }, [setIsUserAddOnModalOpen]);

  const updateStoreOnUnmount = useCallback(() => dispatch(sellerSubscriptionPlanInfoUpdated()), [dispatch]);

  const cancel3plBundleAsync = useCallback(
    () =>
      new Promise((resolve) => {
        remove3plBundle()
          .then(() => {
            NotificationService.showNotification(removeProviderBundleSuccessNotification);
            setIs3plPlanEnabled(false);
            resolve(true);
          })
          .catch((error) => {
            setErrorDuring3plPlanRemoval(extractErrorDataFromRemovalFailureResponse(error));
            setCurrentPage(CANCEL_ACCOUNT_VIA_SUPPORT);
            NotificationService.showNotification(removeProviderBundleFailureNotification);
            resolve(false);
          });
      }),
    [setCurrentPage, setErrorDuring3plPlanRemoval, setIs3plPlanEnabled],
  );

  const cancelSubscriptionCall = useCallback(
    async (payload) => {
      let didCancelSubscriptionAddons = true;

      if (is3plPlanEnabled) {
        didCancelSubscriptionAddons = await cancel3plBundleAsync();
      }

      if (!didCancelSubscriptionAddons) {
        NotificationService.showNotification(cancellationRequestErrorNotification);
        return;
      }

      cancelSubscription(payload)
        .then(() => {
          dispatch(refreshSellerPlanAndSubscriptionInfo());
          setIsCancellationModalOpen(false);
          Segment.trackEvent(SUBSCRIPTION_CANCELLATION_COMPLETE, {
            source: 'cancel subscription modal',
            cancellationInfo: payload,
          });
        })
        .catch(() => {
          NotificationService.showNotification(cancellationRequestErrorNotification);
        });
    },
    [is3plPlanEnabled, cancel3plBundleAsync, dispatch, setIsCancellationModalOpen],
  );

  const reloadData = useCallback(async () => {
    return Promise.all([
      getBillingInfo()
        .then((response) => {
          setBillingInfo(response?.data || undefined);
          return response?.data;
        })
        .catch((errors) => {
          LogService.logError(errors);
        }),
      getPaymentSummaryListApi()
        .then((response) => {
          const paymentMethodsList = [...(response?.activePaymentMethods || []), ...(response?.recentPaymentMethods || [])];
          setPaymentMethodDetails(paymentMethodsList);
          dispatch(setPaymentMethods(paymentMethodsList));
          return paymentMethodsList;
        })
        .catch((errors) => {
          LogService.logError(errors);
        }),
    ]).finally(() => {
      setIsBillingInfoLoading(false);
      setIsPaymentSummaryListLoading(false);
    });
  }, [dispatch, setBillingInfo, setIsBillingInfoLoading, setIsPaymentSummaryListLoading, setPaymentMethodDetails]);

  // effects
  useEffect(() => {
    if (!Object.keys(subscriptionData).length) {
      manageSubscription()
        .then((response) => {
          dispatch(updateSellerSubscriptionData(response?.data));
          setIsSubscriptionDataLoading(false);
        })
        .catch((errors) => {
          LogService.logError(errors);
        });
    } else {
      setIsSubscriptionDataLoading(false);
    }
    reloadData();
  }, [dispatch, setBillingInfo, subscriptionData, setIsSubscriptionDataLoading, setIsBillingInfoLoading, reloadData]);

  useEffect(() => {
    return updateStoreOnUnmount;
  }, [updateStoreOnUnmount]);

  const value = useMemo(
    () => ({
      billingInfo,
      setBillingInfo,
      isBillingInfoLoading,
      isUserAddOnModalOpen,
      setIsUserAddOnModalOpen,
      isCancellationModalOpen,
      setIsCancellationModalOpen,
      desiredPlanCode,
      setDesiredPlanCode,
      followUpCancelReasonId,
      setFollowUpCancelReasonId,
      currentPage,
      setCurrentPage,
      followUpExplain,
      setFollowUpExplain,
      cancelReasonId,
      setCancelReasonId,
      comments,
      setComments,
      cancellationInfo,
      setCancellationInfo,
      sellerStampsProviders,
      setSellerStampsProviders,
      cancelSubscriptionCall,
      reloadData,
      desiredNumberOfAdditionalUsers,
      setDesiredNumberOfAdditionalUsers,
      isBillingEmailModalOpen,
      setIsBillingEmailModalOpen,
      is3plPlanEnabled,
      setIs3plPlanEnabled,
      errorDuring3plPlanRemoval,
      billingInfoErrorType,
      setBillingInfoErrorType,
      byoaEnabled,
      setByoaEnabled,
      defaultSuggestedPlanId,
      selectedPlanId,
      setSelectedPlanId,
      pendingDateTimeUTC,
      pendingPlanIdInGrid,
      closeUserAddonModal,
      setPendingDateTimeUTC,
      isSubscriptionDataLoading,
      isPlanInfoLoading,
      paymentMethodDetails,
      isPaymentSummaryListLoading,
      setIsPaymentSummaryListLoading,
      currentBillingPlanId,
      hasLegacyRecurlyBillingInfo,
      isMissingPaymentMethod,
      hasActiveFreightPaymentMethod,
    }),
    [
      billingInfo,
      setBillingInfo,
      isBillingInfoLoading,
      isUserAddOnModalOpen,
      setIsUserAddOnModalOpen,
      isCancellationModalOpen,
      setIsCancellationModalOpen,
      desiredPlanCode,
      setDesiredPlanCode,
      followUpCancelReasonId,
      setFollowUpCancelReasonId,
      currentPage,
      setCurrentPage,
      followUpExplain,
      setFollowUpExplain,
      cancelReasonId,
      setCancelReasonId,
      comments,
      setComments,
      cancellationInfo,
      setCancellationInfo,
      sellerStampsProviders,
      setSellerStampsProviders,
      cancelSubscriptionCall,
      reloadData,
      desiredNumberOfAdditionalUsers,
      setDesiredNumberOfAdditionalUsers,
      isBillingEmailModalOpen,
      setIsBillingEmailModalOpen,
      is3plPlanEnabled,
      setIs3plPlanEnabled,
      errorDuring3plPlanRemoval,
      billingInfoErrorType,
      setBillingInfoErrorType,
      byoaEnabled,
      setByoaEnabled,
      selectedPlanId,
      defaultSuggestedPlanId,
      setSelectedPlanId,
      pendingDateTimeUTC,
      pendingPlanIdInGrid,
      closeUserAddonModal,
      setPendingDateTimeUTC,
      isSubscriptionDataLoading,
      isPlanInfoLoading,
      paymentMethodDetails,
      isPaymentSummaryListLoading,
      setIsPaymentSummaryListLoading,
      currentBillingPlanId,
      hasLegacyRecurlyBillingInfo,
      isMissingPaymentMethod,
      hasActiveFreightPaymentMethod,
    ],
  );

  return <PaymentAndSubscriptionMegaContext.Provider value={value}>{children}</PaymentAndSubscriptionMegaContext.Provider>;
};

PaymentAndSubscriptionMegaContextProvider.propTypes = {
  children: ChildrenPropType.isRequired,
};
